<template>
  <div v-if="tournament">
    <!-- The main layout for the tournament -->
    <md-card>
      <!-- The title area-->
      <md-card-area md-inset>
        <md-card-header>
          <md-card-header-text>
            <div class="md-title">
              <img class="sport" :src="icon(tournament)">
              {{tournament.name}}
              <md-tooltip>{{tournament.sport}} - {{tournament.id}}</md-tooltip>

              <span>
                <md-progress-bar v-if="$apollo.loading" md-mode="query"/>
              </span>
            </div>
            <div class="md-subhead">
              <md-icon>location_on</md-icon>
              <span>{{(tournament.location && tournament.location.name) || "unknown location"}}</span>
            </div>
            <div class="md-subhead">
              <md-icon>date_range</md-icon>
              <span>{{tournamentDates}}</span>
            </div>
          </md-card-header-text>

          <!-- Tournament actions menu -->
          <md-menu v-if="isMine(tournament)" md-size="auto" md-direction="bottom-end">
            <md-button class="md-icon-button" md-menu-trigger>
              <md-icon>more_vert</md-icon>
            </md-button>

            <md-menu-content>
              <md-menu-item>
                <span>Generate draw</span>
                <md-icon>ac_unit</md-icon>
              </md-menu-item>
              <md-menu-item
                class="md-inset md-accent"
                @click="showFriendlyKODialog = true"
              >Friendly Knockout</md-menu-item>
              <md-menu-item class="md-inset" @click="showKODialog = true">Knockout</md-menu-item>
              <md-menu-item class="md-inset" @click="showRRDialog = true">Round Robin</md-menu-item>

              <md-divider class="md-inset"></md-divider>

              <md-menu-item @click="showConfirmResetDraw = true">
                <span>Reset draw</span>
                <md-icon>redo</md-icon>
              </md-menu-item>

              <md-menu-item @click="showSimulationDialog = true">
                <span>Simulate play</span>
                <md-icon>compare_arrows</md-icon>
              </md-menu-item>

              <md-menu-item v-if="tournament.isPublic" @click="togglePublicity">
                <span>Make private</span>
                <md-icon>vpn_lock</md-icon>
              </md-menu-item>
              <md-menu-item v-else @click="togglePublicity">
                <span>Make public</span>
                <md-icon>public</md-icon>
              </md-menu-item>

              <md-divider class="md-inset"></md-divider>

              <md-menu-item @click="showEditTournamentDialog = true">
                <span>Edit</span>
                <md-icon>edit</md-icon>
              </md-menu-item>

              <md-menu-item @click="showConfirmDelete = true">
                <span>Delete</span>
                <md-icon>delete</md-icon>
              </md-menu-item>
            </md-menu-content>
          </md-menu>
        </md-card-header>
        <md-card-content>{{tournament.description}}</md-card-content>

        <!-- Quick Actions -->
        <md-card-actions>
          <!-- Chips as status indicator -->
          <md-chip v-if="tournament.format">
            <md-icon>ac_unit</md-icon>
            <span>{{truncate(tournament.format, 8)}}</span>
            <md-tooltip>Tournament format - {{tournament.format}}</md-tooltip>
          </md-chip>

          <!--
          <md-chip v-if="tournament.sport">
            <md-icon>directions_run</md-icon>
            <span>{{truncate(tournament.sport, 10)}}</span>
            <md-tooltip>Sport - {{tournament.sport}}</md-tooltip>
          </md-chip>
          -->

          <md-chip class="md-primary" v-if="tournament.isPublic">
            <md-icon>public</md-icon>
            <span>Public</span>
            <md-tooltip>This tournament is visible to the public</md-tooltip>
          </md-chip>
          <md-chip class="md-accent" v-else>
            <md-icon>vpn_lock</md-icon>
            <span>Private</span>
            <md-tooltip>Only you can see this tournament</md-tooltip>
          </md-chip>

          <md-button class="md-icon-button" @click="refetch">
            <md-icon>refresh</md-icon>
          </md-button>
        </md-card-actions>
      </md-card-area>

      <!-- The matches, competitors, etc. tabs -->
      <md-card-content>
        <md-tabs>
          <md-tab
            id="tab-matches"
            :md-label="matchesTabTitle(tournament)"
            md-icon="play_circle_outline"
          >
            <TournamentMatches :tournament="tournament"/>
          </md-tab>

          <md-tab id="tab-diagram" md-icon="playlist_play" :md-label="flightsTabTitle(tournament)">
            <TournamentFlights :tournament="tournament"/>
          </md-tab>

          <md-tab id="tab-competitors" :md-label="competitorsTabTitle(tournament)" md-icon="people">
            <TournamentCompetitors :tournament="tournament"/>
          </md-tab>

          <md-tab id="tab-details" md-icon="details">
            <TournamentDetails :tournament="tournament"/>
          </md-tab>

          <md-tab v-if="isMine(tournament)" id="tab-venues" md-icon="gps_fixed">
            <TournamentVenues :tournament="tournament"/>
          </md-tab>
        </md-tabs>
      </md-card-content>
    </md-card>

    <!-- Dialogs -->
    <GenerateKODrawDialog
      :tournament="tournament"
      :show="showKODialog"
      @onClose="showKODialog = false"
    />
    <GenerateFriendlyKODrawDialog
      :tournament="tournament"
      :show="showFriendlyKODialog"
      @onClose="showFriendlyKODialog = false"
    />
    <GenerateRRDrawDialog
      :tournament="tournament"
      :show="showRRDialog"
      @onClose="showRRDialog = false"
    />
    <SimulatePlayDialog
      :tournament="tournament"
      :show="showSimulationDialog"
      @onClose="showSimulationDialog = false"
    />
    <md-dialog-confirm
      :md-active.sync="showConfirmResetDraw"
      md-title="Reset draw?"
      md-content="Resetting the draw will delete all matches from the tournament... forever!"
      md-confirm-text="Confirm"
      md-cancel-text="Cancel"
      @md-confirm="resetDraw"
    />
    <md-dialog-confirm
      :md-active.sync="showConfirmDelete"
      md-title="Delete tournament?"
      md-content="Delete the tournament and all its matches and competitors forever!"
      md-confirm-text="Confirm"
      md-cancel-text="Cancel"
      @md-confirm="deleteTournament"
    />
    <UpsertTournamentDialog
      :show="showEditTournamentDialog"
      @onClose="showEditTournamentDialog = false"
      :tournament="tournament"
    />
  </div>
  <div v-else>
    <md-progress-spinner class="wait" :md-diameter="100" :md-stroke="10" md-mode="indeterminate"/>
  </div>
</template>

<style lang="scss" scoped>
.md-card {
  width: 100%;
  margin: 0px !important;
  display: inline-block;
  vertical-align: top;
}
.wait {
  margin: 2em;
}
</style>

<script>
import gql from "graphql-tag";
import {
  tournamentQuery,
  tournamentMainFragment,
  tournamentMatchFragment
} from "@/graphql/tournament";
import { TournamentTools, shortDate, truncate } from "@/js/helper";
import TournamentDetails from "./tournamentComponents/tournamentDetails";
import TournamentCompetitors from "./tournamentComponents/tournamentCompetitors";
import TournamentMatches from "./tournamentComponents/tournamentMatches";
import TournamentFlights from "./tournamentComponents/tournamentFlights";
import TournamentVenues from "./tournamentComponents/tournamentVenues";
import { setTimeout } from "timers";
import GenerateKODrawDialog from "@/components/dialogs/generateKODrawDialog.vue";
import GenerateFriendlyKODrawDialog from "@/components/dialogs/generateFriendlyKODrawDialog.vue";
import GenerateRRDrawDialog from "@/components/dialogs/generateRRDrawDialog.vue";
import SimulatePlayDialog from "@/components/dialogs/simulatePlayDialog.vue";
import UpsertTournamentDialog from "@/components/dialogs/upsertTournamentDialog.vue";

export default {
  name: "Tournament",
  components: {
    TournamentDetails,
    TournamentCompetitors,
    TournamentMatches,
    TournamentFlights,
    TournamentVenues,
    GenerateKODrawDialog,
    GenerateFriendlyKODrawDialog,
    GenerateRRDrawDialog,
    SimulatePlayDialog,
    UpsertTournamentDialog
  },

  apollo: {
    tournament() {
      // this query has to be a function to be called after the component is created, giving access to the other properties
      return {
        query: tournamentQuery,
        variables() {
          return {
            id: this.tournamentId
          };
        }
      };
    }
  },

  data: function() {
    return {
      showKODialog: false,
      showFriendlyKODialog: false,
      showRRDialog: false,
      showSimulationDialog: false,
      showConfirmResetDraw: false,
      showConfirmDelete: false,
      showEditTournamentDialog: false
    };
  },

  computed: {
    currentUser: {
      get() {
        return this.$store.state.currentUser;
      }
    },
    tournamentId: {
      get() {
        return this.$store.getters.currentTournamentId;
      }
    },
    tournamentDates: {
      get() {
        return `${
          this.tournament.startDate
            ? shortDate(this.tournament.startDate)
            : "???"
        } - ${
          this.tournament.endDate ? shortDate(this.tournament.endDate) : "???"
        }`;
      }
    }
  },

  methods: {
    truncate: truncate,
    isMine: TournamentTools.isMine,
    icon: TournamentTools.icon,
    resetDraw() {
      return this.$apollo
        .mutate({
          mutation: gql`
            mutation($tournamentId: ID!) {
              resetDraw(tournamentId: $tournamentId) {
                ...TournamentMatch
              }
            }
            ${tournamentMatchFragment}
          `,
          variables: {
            tournamentId: this.tournament.id
          }
        })
        .then(result => {
          this.$root.$emit("updateTournament", this.tournament.id);
          this.$store.commit("setNotification", `Draw has been reset`);
        })
        .catch(err => {
          console.log(err);
          window.alert("Failed to reset draw");
        });
    },
    deleteTournament() {
      return this.$apollo
        .mutate({
          mutation: gql`
            mutation($tournamentId: ID!) {
              deleteTournament(tournamentId: $tournamentId)
            }
          `,
          variables: {
            tournamentId: this.tournament.id
          }
        })
        .then(result => {
          this.$store.commit("setNotification", `Tournament has been deleted`);
          this.$router.push({ path: "/myTournaments" });
        })
        .catch(err => {
          console.log(err);
          window.alert("Failed to delete tournament");
        });
    },
    togglePublicity() {
      return this.$apollo
        .mutate({
          mutation: gql`
            mutation($tournamentId: ID!, $isPublic: Boolean!) {
              toggleTournamentPublicity(
                tournamentId: $tournamentId
                isPublic: $isPublic
              ) {
                ...TournamentMain
              }
            }
            ${tournamentMainFragment}
          `,
          variables: {
            tournamentId: this.tournament.id,
            isPublic: !this.tournament.isPublic
          }
        })
        .then(result => {
          //this.$root.$emit("updateTournament", this.tournament.id);
          this.$store.commit(
            "setNotification",
            `Tournament publicity state has been changed`
          );
        })
        .catch(err => {
          console.log(err);
          window.alert("Failed to change tournament publicity state");
        });
    },
    matchesTabTitle(t) {
      if (t && t.matches) return `${t.matches.length}`;
      return "";
    },
    competitorsTabTitle(t) {
      if (t && t.competitors) return `${t.competitors.length}`;
      return "";
    },
    flightsTabTitle(t) {
      if (t && t.flights) return `${t.flights.length}`;
      return "";
    },
    refetch() {
      if (this.$apollo.queries.tournament)
        this.$apollo.queries.tournament.refetch();
      //this.$store.commit("setNotification", "Tournament refetched");
    }
  },

  created() {
    // This is a temporary way of updating the the tournament data (refetching on local change).
    // Ideally we subscribe to tournament-update events and refetch then, but firebase cloud functions
    // doesn't support web sockets for graphql subscriptions.
    // An alternative is to move the api off Firebase.
    // Another alternative is to do the subscription through Firebase events.
    this.$root.$on("updateTournament", id => {
      if (id === this.tournamentId)
        //setTimeout(() => {
        this.refetch();
      //}, 200);
    });
  }
};
</script>




// WEBPACK FOOTER //
// Tournament.vue?a40998ac